/**
 * AgentPortal.js
 *
 * Agent interface for handling customer handoffs.
 * Rendered inside AppLayout at /agent — uses the app's existing authentication.
 *
 * Props:
 *  - user: Current authenticated user object from App.js { username, role, host_tenant_id, permissions }
 *
 * Features:
 *  - Workflow selector (agents see workflows assigned to their tenant)
 *  - Live handoff queue with auto-refresh
 *  - Real-time chat with users (SSE streaming)
 *  - Resolve handoffs and resume bot workflows
 *  - Agent status (online/away/offline)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Headphones, Send, RefreshCw,
  Clock, CheckCircle2, Search, ChevronDown,
  ArrowLeft, User, Wifi, WifiOff, Coffee, Building2
} from 'lucide-react';
import { apiCall, apiCallJson } from './Api';
import { getCurrentToken } from './SessionManager';

// ==================== Priority Badge ====================
const PriorityBadge = ({ priority }) => {
  const colors = {
    urgent: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    normal: 'bg-blue-100 text-blue-700',
    low: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${colors[priority] || colors.normal}`}>
      {priority || 'normal'}
    </span>
  );
};

// ==================== Channel Badge ====================
const ChannelBadge = ({ channel }) => {
  const labels = { web: 'WEB', whatsapp: 'WA', telegram: 'TG', facebook: 'FB', instagram: 'IG' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full text-[10px] text-gray-600 font-medium">
      {labels[channel] || channel?.toUpperCase()?.slice(0, 3) || 'CH'} {channel || 'web'}
    </span>
  );
};

// ==================== Chat Panel ====================
const ChatPanel = ({ handoff, workflowId, agentName, onResolve, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);
  const messagesEndRef = useRef(null);
  const sseRef = useRef(null);

  const userId = handoff.user_id;
  const channel = handoff.channel || 'web';

  // Load history
  useEffect(() => {
    (async () => {
      try {
        const data = await apiCallJson(
          `/api/chatbot/workflows/${workflowId}/sessions/${channel}/${encodeURIComponent(userId)}/history?limit=200`
        );
        setMessages(data.messages || []);
      } catch {
        setMessages((handoff.message_history || []).map((m, i) => ({
          id: i, role: m.role, content: m.content, timestamp: m.timestamp
        })));
      }
    })();
  }, [handoff, workflowId]);

  // SSE for live messages
  useEffect(() => {
    const token = getCurrentToken();
    const sseUrl = `/api/chatbot/workflows/${workflowId}/handoff/stream?user_id=${encodeURIComponent(userId)}&channel=${encodeURIComponent(channel)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(sseUrl);
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Handoff stream sends {messages: [{role, content, timestamp, ...}]}
        const msgs = data.messages || (data.role ? [data] : []);
        if (msgs.length > 0) {
          setMessages(prev => [...prev, ...msgs.map(m => ({
            id: Date.now() + Math.random(),
            role: m.role || 'user',
            content: m.content || m.text || '',
            timestamp: m.timestamp || new Date().toISOString(),
            agent_id: m.agent_id,
          }))]);
        }
      } catch {}
    };
    sseRef.current = es;
    return () => es.close();
  }, [handoff, workflowId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic add
    setMessages(prev => [...prev, { id: Date.now(), role: 'agent', content: text, timestamp: new Date().toISOString() }]);

    try {
      await apiCall(`/api/chatbot/workflows/${workflowId}/handoff/agent-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, channel, text, agent_id: agentName })
      });
    } catch (err) {
      console.error('Failed to send:', err);
    }
    setSending(false);
  };

  const resolveHandoff = async (resume) => {
    setResolving(true);
    try {
      await apiCall(`/api/chatbot/workflows/${workflowId}/handoff/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, channel, resume_workflow: resume })
      });
      onResolve(userId);
    } catch (err) {
      console.error('Failed to resolve:', err);
    }
    setResolving(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-gray-800 truncate">{userId}</span>
            <ChannelBadge channel={channel} />
            <PriorityBadge priority={handoff.priority} />
          </div>
          {handoff.department && (
            <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {handoff.department}
              {handoff.reason && <span className="ml-2 text-gray-400">-- {handoff.reason}</span>}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => resolveHandoff(true)} disabled={resolving}
            className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Resolve and Resume Bot
          </button>
          <button onClick={() => resolveHandoff(false)} disabled={resolving}
            className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 disabled:opacity-50">
            End Session
          </button>
        </div>
      </div>

      {/* User context variables */}
      {handoff.variables && Object.keys(handoff.variables).length > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex-shrink-0">
          <div className="flex flex-wrap gap-2">
            {Object.entries(handoff.variables).slice(0, 6).map(([k, v]) => (
              <span key={k} className="inline-flex items-center bg-white border border-amber-200 rounded-full px-2 py-0.5 text-[10px]">
                <span className="text-amber-700 font-medium">{k}:</span>
                <span className="text-gray-600 ml-1 max-w-32 truncate">{String(v)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.map((msg, i) => (
          <div key={msg.id || i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${
              msg.role === 'user' ? 'bg-white border border-gray-200 text-gray-800' :
              msg.role === 'agent' ? 'bg-amber-600 text-white' :
              'bg-blue-100 text-blue-800'
            }`}>
              <div className="text-[10px] mb-0.5 opacity-70 font-medium">
                {msg.role === 'user' ? 'Customer' : msg.role === 'agent' ? `${agentName} (You)` : 'Bot'}
              </div>
              <div className="whitespace-pre-wrap">{msg.content}</div>
              {msg.timestamp && (
                <div className="text-[9px] opacity-50 mt-1 text-right">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 bg-white border-t border-gray-200 flex-shrink-0">
        <div className="flex gap-2">
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
          <button onClick={sendMessage} disabled={!input.trim() || sending}
            className="px-4 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Handoff Queue ====================
const HandoffQueue = ({ handoffs, loading, onRefresh, onSelect }) => {
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  const departments = [...new Set(handoffs.map(h => h.department).filter(Boolean))];
  const filtered = handoffs.filter(h => {
    if (search && !h.user_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (deptFilter && h.department !== deptFilter) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex gap-2 flex-shrink-0">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by user ID..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        {departments.length > 0 && (
          <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <button onClick={onRefresh} className="p-2 hover:bg-gray-100 rounded-lg text-amber-600">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Headphones className="h-12 w-12 mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 text-sm font-medium">No active handoffs</p>
            <p className="text-gray-400 text-xs mt-1">New handoff requests will appear here automatically</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((h, i) => {
              const waitTime = h.timestamp ? Math.round((Date.now() - new Date(h.timestamp)) / 60000) : 0;
              return (
                <button key={i} onClick={() => onSelect(h)}
                  className="w-full text-left px-4 py-4 hover:bg-amber-50/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-800">{h.user_id}</span>
                        <ChannelBadge channel={h.channel} />
                        <PriorityBadge priority={h.priority} />
                      </div>
                      {h.department && (
                        <div className="text-xs text-amber-700 flex items-center gap-1 mb-1">
                          <Building2 className="h-3 w-3" /> {h.department}
                        </div>
                      )}
                      {h.reason && (
                        <div className="text-xs text-gray-500 line-clamp-1">{h.reason}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <div className={`text-xs font-medium ${waitTime > 5 ? 'text-red-600' : 'text-gray-500'}`}>
                        {waitTime > 0 ? `${waitTime}m ago` : 'Just now'}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {h.message_history?.length || 0} msgs
                      </div>
                    </div>
                  </div>
                  {h.message_history?.length > 0 && (
                    <div className="mt-2 bg-gray-50 rounded-lg p-2 text-xs text-gray-500 line-clamp-2">
                      <span className="font-medium">Last:</span> {h.message_history.slice(-1)[0]?.content}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Workflow Selector ====================
const WorkflowSelector = ({ workflows, selected, onSelect }) => (
  <select value={selected || ''} onChange={(e) => onSelect(e.target.value)}
    className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-48">
    <option value="">Select Workflow</option>
    {workflows.map(wf => (
      <option key={wf.id} value={wf.id}>{wf.name} ({wf.status})</option>
    ))}
  </select>
);

// ==================== Agent Status ====================
const AgentStatus = ({ status, onChange }) => {
  const statuses = [
    { value: 'online', label: 'Online', icon: Wifi, color: 'text-green-600 bg-green-50' },
    { value: 'away', label: 'Away', icon: Coffee, color: 'text-amber-600 bg-amber-50' },
    { value: 'offline', label: 'Offline', icon: WifiOff, color: 'text-gray-500 bg-gray-50' },
  ];
  const current = statuses.find(s => s.value === status) || statuses[0];
  const Icon = current.icon;

  return (
    <div className="relative group">
      <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${current.color} border`}>
        <Icon className="h-3.5 w-3.5" />
        {current.label}
        <ChevronDown className="h-3 w-3" />
      </button>
      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 hidden group-hover:block z-10">
        {statuses.map(s => (
          <button key={s.value} onClick={() => onChange(s.value)}
            className="w-full px-4 py-2 text-left text-xs hover:bg-gray-50 flex items-center gap-2">
            <s.icon className={`h-3.5 w-3.5 ${s.color.split(' ')[0]}`} />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ==================== Main Agent Portal ====================
const AgentPortal = ({ user }) => {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [handoffs, setHandoffs] = useState([]);
  const [selectedHandoff, setSelectedHandoff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agentStatus, setAgentStatus] = useState('online');

  const agentName = user?.username || 'Agent';

  // Load workflows
  useEffect(() => {
    apiCallJson('/api/chatbot/workflows')
      .then(data => setWorkflows(data.workflows || []))
      .catch(console.error);
  }, []);

  // Load handoffs for selected workflow
  const loadHandoffs = useCallback(async () => {
    if (!selectedWorkflow) return;
    setLoading(true);
    try {
      const data = await apiCallJson(`/api/chatbot/workflows/${selectedWorkflow}/handoff/active`);
      setHandoffs(data.handoffs || []);
    } catch (err) {
      console.error('Failed to load handoffs:', err);
    }
    setLoading(false);
  }, [selectedWorkflow]);

  useEffect(() => {
    loadHandoffs();
    const interval = setInterval(loadHandoffs, 8000);
    return () => clearInterval(interval);
  }, [loadHandoffs]);

  const handleResolve = (userId) => {
    setHandoffs(prev => prev.filter(h => h.user_id !== userId));
    setSelectedHandoff(null);
  };

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <Headphones className="h-4 w-4 text-amber-700" />
            </div>
            <span className="font-bold text-gray-800 text-lg">Agent Portal</span>
          </div>
          <div className="h-6 w-px bg-gray-300" />
          <WorkflowSelector workflows={workflows} selected={selectedWorkflow} onSelect={setSelectedWorkflow} />
        </div>

        <div className="flex items-center gap-4">
          {handoffs.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-600" />
              </span>
              <span className="text-xs font-semibold text-amber-800">{handoffs.length} active</span>
            </div>
          )}
          <AgentStatus status={agentStatus} onChange={setAgentStatus} />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-gray-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">{agentName}</span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {!selectedWorkflow ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Building2 className="h-16 w-16 mx-auto text-gray-200 mb-4" />
              <h2 className="text-xl font-semibold text-gray-500">Select a Workflow</h2>
              <p className="text-gray-400 mt-1 text-sm">Choose a workflow from the dropdown above to view handoffs</p>
            </div>
          </div>
        ) : selectedHandoff ? (
          <div className="flex-1 bg-white">
            <ChatPanel
              handoff={selectedHandoff} workflowId={selectedWorkflow}
              agentName={agentName} onResolve={handleResolve}
              onBack={() => setSelectedHandoff(null)}
            />
          </div>
        ) : (
          <div className="flex-1">
            <HandoffQueue
              handoffs={handoffs} loading={loading}
              onRefresh={loadHandoffs} onSelect={setSelectedHandoff}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentPortal;
